import { Link } from "react-router-dom";
import { ArrowRight, PenLine, FileSearch, Sparkles } from "lucide-react";

const stages = [
  {
    icon: PenLine,
    label: "Stage 01",
    title: "Write Your Book",
    lead: "For the aspiring author with a message and a blank page.",
    body: "Start today on our interactive platform. It turns your professional journey into a book concept that actually sells you — author positioning, the reader you are writing for, and a chapter-by-chapter outline — with expert human review available when you want to escalate.",
    points: ["Interactive, start-immediately platform", "Author positioning and reader definition", "Chapter outline and writing roadmap"],
    to: "/write-your-book",
    cta: "Start your book today",
  },
  {
    icon: FileSearch,
    label: "Stage 02",
    title: "Manuscript Review",
    lead: "For the author who already has a manuscript in progress.",
    body: "Your manuscript deserves an expert first read. Upload your draft and receive paragraph-level editorial feedback — what is working, what is vague, and what needs reworking — each note tied to a specific publishing principle rather than generic advice.",
    points: ["Paragraph-level editorial notes", "Chapter promise and structure checks", "Clear revision path, chapter by chapter"],
    to: "/first-draft",
    cta: "Review my manuscript",
  },
  {
    icon: Sparkles,
    label: "Stage 03",
    title: "Extend",
    lead: "For the published author whose book should be earning.",
    body: "We turn your finished book into an interactive, AI-powered platform. Readers complete personalised assessments, receive tailored reports, and step into your coaching or consulting offer — while you gain a qualified pipeline and recurring revenue.",
    points: ["Interactive assessments and reports", "AI trained on your own content", "Qualified leads and recurring revenue"],
    to: "/extend",
    cta: "Explore Extend",
  },
];

const JourneySection = () => {
  return (
    <section id="journey" aria-labelledby="journey-heading" className="py-20 md:py-28 border-t border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="flex items-center gap-3 mb-5">
            <span className="rule-gold" aria-hidden />
            <p className="eyebrow">Three stages, one arc</p>
          </div>
          <h2 id="journey-heading" className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1] mb-5">
            Wherever your book is today, there is a next step.
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Most authors arrive at one of three moments. Find yours below — each stage stands alone,
            and together they take a message from idea to income.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {stages.map(({ icon: Icon, label, title, lead, body, points, to, cta }, i) => (
            <article
              key={to}
              className="card-noir-hover group relative flex flex-col p-7 md:p-8 animate-fade-in"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <span className="absolute inset-x-0 top-0 h-px gold-hairline opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />

              <div className="flex items-center justify-between mb-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-sm border border-border bg-background text-gold">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
              </div>

              <h3 className="text-2xl md:text-[1.7rem] font-semibold mb-3">{title}</h3>
              <p className="font-serif italic text-gold/90 mb-5">{lead}</p>
              <p className="text-[15px] text-muted-foreground leading-relaxed mb-7">{body}</p>

              <ul className="space-y-3 mb-8">
                {points.map((point) => (
                  <li key={point} className="flex gap-3 text-sm text-foreground/85">
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gold" aria-hidden />
                    {point}
                  </li>
                ))}
              </ul>

              <Link
                to={to}
                className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-gold hover:gap-3 transition-all"
              >
                {cta}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default JourneySection;
